import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Landmark, Hotel, Briefcase, Building } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
  overline: { es: "Con quién trabajamos", en: "Who we work with" },
  title: { es: "¿Para quién es?", en: "Who Is This For?" },
  badge: { es: "Participantes formados", en: "Professionals trained" },
  audiences: {
    es: [
      { title: "Convention Bureaus y destinos", description: "Oficinas de turismo, ministerios y entidades gubernamentales que buscan posicionar su destino y atraer eventos internacionales.", color: "#2ED3C6" },
      { title: "Cadenas hoteleras y recintos", description: "Hoteles, centros de convenciones, recintos feriales y venues que quieren profesionalizar sus áreas MICE y eventos.", color: "#F59E0B" },
      { title: "DMCs, agencias y OPCs", description: "Operadoras profesionales de congresos, DMCs y agencias de eventos que buscan elevar la operación y estándares de su equipo.", color: "#2ED3C6" },
      { title: "Corporativos y asociaciones", description: "Empresas con áreas de eventos internos y asociaciones profesionales que organizan congresos y encuentros recurrentes.", color: "#F59E0B" },
    ],
    en: [
      { title: "Convention Bureaus & destinations", description: "Tourism offices, ministries, and government entities seeking to position their destination and attract international events.", color: "#2ED3C6" },
      { title: "Hotel chains & venues", description: "Hotels, convention centers, exhibition venues and event spaces that want to professionalize their MICE and events teams.", color: "#F59E0B" },
      { title: "DMCs, agencies & PCOs", description: "Professional Congress Organizers, DMCs, and event agencies looking to raise the operational and quality standards of their teams.", color: "#2ED3C6" },
      { title: "Corporates & associations", description: "Companies with internal event departments and professional associations organizing recurring congresses and gatherings.", color: "#F59E0B" },
    ],
  },
};

const AudienceSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const audiences = t(translations.audiences);
  const icons = [Landmark, Hotel, Briefcase, Building];

  return (
    <section data-testid="audience-section" ref={ref} className="py-24 md:py-32 bg-[#EEF1F4]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div initial={{ opacity: 0, x: -40 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8 }} className="relative">
            <div className="relative rounded-2xl overflow-hidden">
              <img src="https://images.unsplash.com/photo-1775163560631-6ff15eb2fa1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBuZXR3b3JraW5nJTIwZXZlbnR8ZW58MHx8fHwxNzc1NzU1MTExfDA&ixlib=rb-4.1.0&q=85" alt="Profesionales MICE de Latinoamérica formados por Industria de Reuniones" width="800" height="500" loading="lazy" decoding="async" className="w-full h-[400px] md:h-[500px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3B]/60 to-transparent" />
            </div>
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={isInView ? { opacity: 1, scale: 1 } : {}} transition={{ duration: 0.6, delay: 0.4 }} className="absolute -bottom-6 -right-6 bg-[#0B1F3B] text-white p-6 rounded-xl shadow-2xl">
              <div className="text-4xl font-display font-bold text-[#2ED3C6]">+1,100</div>
              <div className="text-sm font-body text-white/70">{t(translations.badge)}</div>
            </motion.div>
          </motion.div>

          <div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
              <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
                <span className="w-8 h-[2px] bg-[#2ED3C6]" />
                {t(translations.overline)}
              </span>
              <h2 data-testid="audience-title" className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-8">
                {t(translations.title)}
              </h2>
            </motion.div>

            <div className="space-y-4">
              {audiences.map((audience, index) => {
                const Icon = icons[index];
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: 20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                    data-testid={`audience-card-${index}`}
                    className="group flex items-start gap-4 p-5 bg-white rounded-xl border border-gray-100 hover:shadow-lg hover:border-[#2ED3C6]/30 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${audience.color}20` }}>
                      <Icon className="w-6 h-6" style={{ color: audience.color }} />
                    </div>
                    <div>
                      <h3 className="font-display text-lg text-[#0B1F3B] font-medium mb-1">{audience.title}</h3>
                      <p className="font-body text-[#0B1F3B]/60 text-sm">{audience.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AudienceSection;
